import { Component } from 'react';
import Dropzone from 'react-dropzone';
import { FormattedMessage } from 'react-intl';
import _ from 'lodash';
import { UploadSVG } from '../../../../svgComponents/uploadSVG';
import ConfirmationButton from '../../../ui/confirmationButton/ConfirmationButton';
import { LoadingSpinner } from '../../../ui/loadingSpinner';
import { Prompt } from '../../../ui/prompt';
import { IwDynamicFormattedMessage } from '../../../../iwDynamicFormattedMessage/IwDynamicFormattedMessage';
import { BANK_STATMENT_DOC_TYPE } from '../UploadDocumentsModalHelper';
import messages from './DocumentUploader.messages';
import documentTypeMessages from '../chooseDocumentType/ChooseDocumentType.messages';
import styles from './DocumentUploader.module.css';
import { TDocument } from '../UploadDocumentsModal.reducer';

const MAX_FILE_SIZE = 1073741274; //1GB
const ACCEPTED_FILE_TYPES = '.pdf,.png,.csv,.jpg,.jpeg,.xlsx,.docx,.xls';

type TProps = {
    closeModal: () => void;
    goBackToDocumentTypeSelection: () => void;
    uploadDocument: (
        documents: File[],
        documentType: {
            label: string;
            value: string;
        }
    ) => void;

    documentType: { label: string; value: string };
    documentsStatus: TDocument[];
    preventBackNavigation?: boolean;
};

class DocumentUploader extends Component<TProps> {
    renderPendingState(document: TDocument) {
        return (
            <div className={styles.pendingStyle}>
                <div className={styles.documentName}>
                    <FormattedMessage
                        {...messages.uploadingDocument}
                        values={{ document: document.name }}
                    />
                </div>
                <LoadingSpinner className={styles.uploadingDocuments} />
            </div>
        );
    }

    renderSuccessState(document: TDocument) {
        return (
            <div className={styles.successStyle}>
                <div className={styles.documentName}>
                    <FormattedMessage
                        {...messages.uploadedDocument}
                        values={{ document: document.name }}
                    />
                </div>
            </div>
        );
    }

    renderErrorState(document: TDocument) {
        return (
            <div className={styles.errorStyle}>
                <div className={styles.documentName}>
                    <FormattedMessage
                        {...messages.failedDocument}
                        values={{ document: document.name }}
                    />
                </div>
                <div className={styles.errorMessage}>
                    <IwDynamicFormattedMessage
                        messages={messages}
                        messageKey={document.code!}
                    />
                </div>
            </div>
        );
    }

    renderUploadedFilesStatus() {
        const { documentsStatus } = this.props;

        let documentStatus;
        return _.map(
            documentsStatus,
            (document: TDocument, documentKey: string) => {
                if (document.pending) {
                    documentStatus = this.renderPendingState(document);
                } else if (document.success) {
                    documentStatus = this.renderSuccessState(document);
                } else {
                    documentStatus = this.renderErrorState(document);
                }

                return <div key={documentKey}>{documentStatus}</div>;
            }
        );
    }

    onDrop = (acceptedFiles: File[]) => {
        const { documentType } = this.props;
        this.props.uploadDocument(acceptedFiles, documentType!);
    };

    render() {
        const {
            documentType,
            goBackToDocumentTypeSelection,
            preventBackNavigation,
            closeModal
        } = this.props;
        const uploadedFilesStatus = this.renderUploadedFilesStatus();

        return (
            <div data-testid="upload-bank-statements-modal">
                <div
                    className={styles.title}
                    data-testid="upload-document-title"
                >
                    <FormattedMessage
                        {...messages.title}
                        values={{
                            documentType: (
                                <FormattedMessage
                                    {...documentTypeMessages[
                                        documentType!
                                            .label as keyof typeof documentTypeMessages
                                    ]}
                                />
                            )
                        }}
                    />
                </div>

                {_.isEqual(documentType, BANK_STATMENT_DOC_TYPE) ? (
                    <p data-testid="bank-statement-guidance">
                        <FormattedMessage {...messages.bankStatementGuidance} />
                    </p>
                ) : null}

                <div className={styles.uploadedFiles}>
                    {uploadedFilesStatus}
                </div>
                <Dropzone
                    disableClick
                    accept={ACCEPTED_FILE_TYPES}
                    maxSize={MAX_FILE_SIZE}
                    className={styles.dropzone}
                    onDrop={this.onDrop}
                >
                    {({ open }) => (
                        <div className={styles.dropzoneContent}>
                            <UploadSVG />
                            <div className={styles.dropzoneMessage}>
                                <FormattedMessage
                                    {...messages.dragAndDropMessage}
                                />
                            </div>
                            <button
                                className={styles.browseFile}
                                onClick={() => open()}
                            >
                                <FormattedMessage {...messages.browseFile} />
                            </button>
                        </div>
                    )}
                </Dropzone>
                <Prompt className={styles.prompt}>
                    {!preventBackNavigation && (
                        <button onClick={goBackToDocumentTypeSelection}>
                            <FormattedMessage {...messages.back} />
                        </button>
                    )}
                    <ConfirmationButton onClick={closeModal}>
                        <FormattedMessage {...messages.finish} />
                    </ConfirmationButton>
                </Prompt>
            </div>
        );
    }
}

// eslint-disable-next-line import/no-default-export
export default DocumentUploader;
